import React, { useState } from 'react';
import { Card, Button, Table, Badge, Spinner, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { BAOVECAPVIEN_API, DUYETBAOVECAPVIEN_API } from '@app/utils/apiUrl';
import { trangThaiBaoVeCapVienValue, objectTrangThaiBaoVeCapVien } from '@app/utils/define';
import { Get, Put } from '@app/utils/httpProvider';
import moment from 'moment';
import { toast } from 'react-toastify';
import QuyetDinhCapVienModal from './QuyetDinhCapVienModal';
import CongBoTrenWebModal from './CongBoTrenWebModal';
import KetQuaBaoVeCapVienModal from './KetQuaBaoVeCapVienModal';
import TaiLieuKhacModal from './TaiLieuKhacModal';

const DuyetBaoVeCapVien = ({ hoSoId }) => {
    const queryClient = useQueryClient();
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [trangThai, setTrangThai] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const { isLoading, data: queryData, refetch } = useQuery(
        ['duyetBaoVeCapVienData', hoSoId],
        () => Get(`${BAOVECAPVIEN_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId,
            keepPreviousData: true
        }
    );

    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.data?.$values) {
            dataList = queryData.data.$values;
        } else {
            dataList = [queryData];
        }
    }

    React.useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = dataList.find(item => item.id === prev.id);
                    return fresh || dataList[0];
                }
                return dataList[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [dataList]);

    const handleAction = (item) => {
        setDataEdit(item);
        setTrangThai(item.trangThai || trangThaiBaoVeCapVienValue.choQuyetDinh);
        setShowModal(true);
    };

    const submitDuyet = async () => {
        if (!trangThai) {
            toast.warning('Vui lòng chọn trạng thái!');
            return;
        }

        const payload = {
            Id: dataEdit.id,
            HoSoNghienCuuSinhId: hoSoId,
            NgayBaoVe: dataEdit.ngayBaoVe || null,
            TrangThai: trangThai
        };

        setIsSaving(true);
        try {
            const res = await Put(DUYETBAOVECAPVIEN_API.DUYET_HO_SO, dataEdit.id, payload);
            if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id || res.success)) {
                toast.success('Cập nhật trạng thái thành công!');
                queryClient.invalidateQueries(['baoVeCapVienData', hoSoId]);
                refetch();
                setShowModal(false);
            } else {
                toast.error(res?.message || 'Có lỗi xảy ra');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra');
        } finally {
            setIsSaving(false);
        }
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiBaoVeCapVienValue.choQuyetDinh: return <Badge variant="warning">Chờ quyết định</Badge>;
            case trangThaiBaoVeCapVienValue.choBaove: return <Badge variant="info">Chờ bảo vệ</Badge>;
            case trangThaiBaoVeCapVienValue.duDieuKien: return <Badge variant="primary">Đủ điều kiện</Badge>;
            case trangThaiBaoVeCapVienValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="font-weight-bold text-primary m-0">Bảo vệ luận án cấp viện</h5>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th className="text-center" width="50">STT</th>
                                    <th className="text-center">Ngày bảo vệ dự kiến</th>
                                    <th className="text-center">Trạng thái</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataList && dataList.length > 0 ? (
                                    dataList.map((item, index) => (
                                        <tr 
                                            key={item.id}
                                            onClick={() => setSelectedRow(item)}
                                            style={{ cursor: 'pointer' }}
                                            className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                        >
                                            <td className="text-center">{index + 1}</td>
                                            <td className="text-center">
                                                {item.ngayBaoVe ? moment(item.ngayBaoVe).format('DD/MM/YYYY') : ''}
                                            </td>
                                            <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center text-muted">
                                            Chưa có dữ liệu Bảo vệ luận án cấp viện
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="warning" 
                            size="sm" 
                            className="mr-2 text-white"
                            onClick={() => handleAction(selectedRow)}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-tasks mr-1"></i> Cập nhật trạng thái
                        </Button>
                        <Button 
                            variant="primary" 
                            size="sm" 
                            className="mr-2"
                            onClick={() => { setDataEdit(selectedRow); setViewMode('quyetDinhCapVien'); }}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-check-double mr-1"></i> Quyết định cấp viện
                        </Button>
                        <Button 
                            variant="info" 
                            size="sm" 
                            className="mr-2 text-white"
                            onClick={() => { setDataEdit(selectedRow); setViewMode('congBoTrenWeb'); }}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-globe mr-1"></i> Điều kiện tổ chức
                        </Button>
                        <Button 
                            variant="success" 
                            size="sm" 
                            className="mr-2"
                            onClick={() => { setDataEdit(selectedRow); setViewMode('ketQuaBaoVe'); }}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-poll-h mr-1"></i> Kết quả bảo vệ
                        </Button>
                        <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={() => { setDataEdit(selectedRow); setViewMode('taiLieuKhac'); }}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-folder-open mr-1"></i> Tài liệu khác
                        </Button>
                    </div>
                </Card.Footer>
            )}
        </Card>

        {showModal && (
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false} size="md">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Cập nhật trạng thái Bảo vệ luận án cấp viện</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Trạng thái</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={trangThai}
                                onChange={(e) => setTrangThai(e.target.value)}
                            >
                                {objectTrangThaiBaoVeCapVien.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={submitDuyet} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : 'Lưu lại'}
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        {viewMode === 'quyetDinhCapVien' && dataEdit && (
            <QuyetDinhCapVienModal
                dataEdit={dataEdit}
                setViewMode={setViewMode}
                isView={false}
                isUserView={false}
            />
        )}
        {viewMode === 'congBoTrenWeb' && dataEdit && (
            <CongBoTrenWebModal
                dataEdit={dataEdit}
                setViewMode={setViewMode}
                isView={false}
                isUserView={false}
            />
        )}
        {viewMode === 'ketQuaBaoVe' && dataEdit && (
            <KetQuaBaoVeCapVienModal
                dataEdit={dataEdit}
                setViewMode={setViewMode}
                isView={false}
                isUserView={false}
            />
        )}
        {viewMode === 'taiLieuKhac' && dataEdit && (
            <TaiLieuKhacModal
                dataEdit={dataEdit}
                setViewMode={setViewMode}
                isView={false}
                isUserView={false}
            />
        )}
        </>
    );
};

export default DuyetBaoVeCapVien;
